'use client';

import type { ReactNode } from 'react';
import { JSONRenderer } from '../../ToolCallsRenderers/renderers/JSONRenderer';
import { PlainTextRenderer } from '../../ToolCallsRenderers/renderers/PlainTextRenderer';
import type { ParsedWebPage } from '../../ToolCallsRenderers/types';
import type { OutputRenderContext } from '../../tool-config-types';
import { WebPagesRenderer } from './WebPagesRenderer';

const WEB_PAGES_HEADING_PATTERN = /##\s*Web Pages\s*\((\d+)\s+of\s+(\d+)\)/i;

const parseWebPageListFromMarkdown = (
  content: string,
): { webPages: ParsedWebPage[]; totalCount?: number } | null => {
  const headingMatch = content.match(WEB_PAGES_HEADING_PATTERN);
  const itemPattern =
    /-\s+\*\*(.*?)\*\*\s*\n\s*ID:\s*([^\n]+)(?:\n\s*Public:\s*(Yes|No))?(?:\n\s*Role:\s*([^\n]+))?(?:\n\s*Created:\s*([^|\n]+)\s*\|\s*Updated:\s*([^\n]+))?/gi;
  const webPages: ParsedWebPage[] = [];

  for (const match of content.matchAll(itemPattern)) {
    webPages.push({
      url: match[1]?.trim(),
      id: match[2]?.trim(),
      isPubliclyAccessible:
        match[3] === 'Yes' ? true : match[3] === 'No' ? false : undefined,
      role: match[4]?.trim(),
      createdAt: match[5]?.trim(),
      updatedAt: match[6]?.trim(),
    });
  }

  if (webPages.length === 0) {
    return null;
  }

  return {
    webPages,
    totalCount: headingMatch?.[2] ? Number(headingMatch[2]) : undefined,
  };
};

const parseWebPageDetailsFromMarkdown = (
  content: string,
): ParsedWebPage | null => {
  const idMatch = content.match(
    /(?:^|\b)(?:\*\*)?ID(?::\*\*|\*\*:|:)\s*([a-f0-9]{24})/i,
  );
  const urlMatch =
    content.match(
      /(?:^|\b)(?:\*\*)?URL(?::\*\*|\*\*:|:)\s*(https?:\/\/\S+)/i,
    ) ?? content.match(/\*\*(https?:\/\/[^*]+)\*\*/i);
  const roleMatch = content.match(
    /(?:^|\b)(?:\*\*)?Role(?::\*\*|\*\*:|:)\s*([^\n]+)/i,
  );

  if (!idMatch && !urlMatch) {
    return null;
  }

  return {
    id: idMatch?.[1],
    url: urlMatch?.[1]?.replace(/\*\*$/, '').trim(),
    role: roleMatch?.[1]?.replace(/\*\*$/, '').trim(),
  };
};

const parseWebPageFromObject = (content: object): ParsedWebPage | null => {
  const record = content as Record<string, unknown>;
  const textContent =
    typeof record.result === 'string'
      ? record.result
      : typeof record.output === 'string'
        ? record.output
        : typeof record.content === 'string'
          ? record.content
          : undefined;

  if (textContent) {
    return parseWebPageDetailsFromMarkdown(textContent);
  }

  const webPageRecord =
    typeof record.webPage === 'object' && record.webPage !== null
      ? (record.webPage as Record<string, unknown>)
      : typeof record.webPageDetails === 'object' &&
          record.webPageDetails !== null
        ? (record.webPageDetails as Record<string, unknown>)
        : record;
  const id = webPageRecord.id;
  const url = webPageRecord.url;

  if (typeof id !== 'string' && typeof url !== 'string') {
    return null;
  }

  return {
    id: typeof id === 'string' ? id : undefined,
    url: typeof url === 'string' ? url : undefined,
    role:
      typeof webPageRecord.role === 'string' ? webPageRecord.role : undefined,
    isPubliclyAccessible:
      typeof webPageRecord.isPubliclyAccessible === 'boolean'
        ? webPageRecord.isPubliclyAccessible
        : undefined,
  };
};

const parseWebPagesFromObject = (content: object): ParsedWebPage[] | null => {
  const record = content as Record<string, unknown>;
  const value = Array.isArray(record.webPages)
    ? record.webPages
    : Array.isArray(record.results)
      ? record.results
      : Array.isArray(record.pages)
        ? record.pages
        : undefined;

  if (!value) {
    return null;
  }

  const webPages = value
    .map((item) =>
      typeof item === 'object' && item !== null
        ? parseWebPageFromObject(item)
        : null,
    )
    .filter((item): item is ParsedWebPage => item !== null);

  return webPages.length > 0 ? webPages : null;
};

export const renderWebPagesListResult = (
  ctx: OutputRenderContext,
): ReactNode => {
  const { content, onOpenObject } = ctx;

  if (typeof content === 'string') {
    const parsed = parseWebPageListFromMarkdown(content);

    if (parsed) {
      return (
        <WebPagesRenderer
          webPages={parsed.webPages}
          onOpenObject={onOpenObject}
        />
      );
    }
  }

  if (typeof content === 'object' && content !== null) {
    const webPages = parseWebPagesFromObject(content);

    if (webPages) {
      return (
        <WebPagesRenderer webPages={webPages} onOpenObject={onOpenObject} />
      );
    }

    const webPage = parseWebPageFromObject(content);

    if (webPage) {
      return (
        <WebPagesRenderer webPages={[webPage]} onOpenObject={onOpenObject} />
      );
    }

    return <JSONRenderer data={content} />;
  }

  return <PlainTextRenderer content={String(content)} />;
};

export const renderWebPageResult = (ctx: OutputRenderContext): ReactNode => {
  const { content, onOpenObject } = ctx;

  if (typeof content === 'string') {
    const webPage = parseWebPageDetailsFromMarkdown(content);

    if (webPage) {
      return (
        <WebPagesRenderer webPages={[webPage]} onOpenObject={onOpenObject} />
      );
    }
  }

  if (typeof content === 'object' && content !== null) {
    const webPage = parseWebPageFromObject(content);

    if (webPage) {
      return (
        <WebPagesRenderer webPages={[webPage]} onOpenObject={onOpenObject} />
      );
    }

    return <JSONRenderer data={content} />;
  }

  return <PlainTextRenderer content={String(content)} />;
};
